import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaBell, FaShoppingCart, FaStickyNote, FaChartLine, FaCalendarCheck, FaTasks, FaRocket } from 'react-icons/fa';
import { dashboardAPI } from '../services/api';
import { ScrollReveal } from '../shared/components';
import './Dashboard.css';

function Dashboard() {
  const [stats, setStats] = useState({
    reminders_count: 0,
    orders_count: 0,
    notes_count: 0,
    recent_activity: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDashboardData();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        setLoading(false);
        return;
      }

      const response = await dashboardAPI.getDashboard();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (error.response?.status === 401) {
        console.error('Token might be invalid');
      }
    } finally {
      setLoading(false);
    }
  };

  const statsCards = [
    {
      title: 'Aktívne pripomienky',
      value: stats.reminders_count,
      icon: FaBell,
      color: '#ec4899',
      link: '/reminders'
    },
    {
      title: 'Čakajúce objednávky',
      value: stats.orders_count,
      icon: FaShoppingCart,
      color: '#14b8a6',
      link: '/orders'
    },
    {
      title: 'Poznámky',
      value: stats.notes_count,
      icon: FaStickyNote,
      color: '#a78bfa',
      link: '/notes'
    },
    {
      title: 'Dnešné úlohy',
      value: 0,
      icon: FaTasks,
      color: '#f97316',
      link: '/todolist'
    }
  ];

  return (
    <div className="dashboard section">
      <div className="container">
        {/* Header */}
        <ScrollReveal>
          <div className="dashboard-header">
            <div className="flex items-center gap-3 mb-2">
              <div className="icon-container" style={{ background: 'rgba(99, 102, 241, 0.15)' }}>
                <FaRocket />
              </div>
              <h1>Dashboard</h1>
            </div>
            <p className="dashboard-subtitle">
              Vitajte späť, <strong>{localStorage.getItem('username')}</strong>!
            </p>
          </div>
        </ScrollReveal>

        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner-lg"></div>
            <p style={{ marginTop: '1rem', color: 'rgba(226, 232, 240, 0.7)' }}>Načítavam...</p>
          </div>
        ) : (
          <>
            {/* Stats Grid */}
            <ScrollReveal delay={0.1}>
              <div className="grid grid-stats mt-4">
                {statsCards.map((card, index) => {
                  const Icon = card.icon;
                  return (
                    <Link to={card.link} key={index} style={{ textDecoration: 'none' }}>
                      <motion.div
                        className="stat-card"
                        whileHover={{
                          transform: 'translateY(-6px)',
                          borderColor: 'rgba(99, 102, 241, 0.4)'
                        }}
                        whileTap={{ scale: 0.98 }}
                        style={{ cursor: 'pointer' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div>
                            <span style={{
                              color: 'rgba(226, 232, 240, 0.7)',
                              fontSize: '0.9rem',
                              display: 'block',
                              marginBottom: '0.5rem'
                            }}>
                              {card.title}
                            </span>
                            <strong style={{ fontSize: '2rem' }}>{card.value}</strong>
                          </div>
                          <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '14px',
                            background: `${card.color}15`,
                            border: `1px solid ${card.color}30`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: card.color,
                            fontSize: '1.5rem'
                          }}>
                            <Icon />
                          </div>
                        </div>
                      </motion.div>
                    </Link>
                  );
                })}
              </div>
            </ScrollReveal>

            {/* Widgets Section */}
            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', marginTop: '2rem' }}>
              <ScrollReveal delay={0.2}>
                <div className="glass-panel">
                  <div className="widget-header">
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <FaChartLine style={{ color: 'var(--primary-light)' }} />
                      Prehľad aktivít
                    </h3>
                  </div>
                  <div className="divider mt-3 mb-3"></div>
                  <div className="widget-content">
                    <div style={{
                      padding: '3rem 1rem',
                      textAlign: 'center',
                      background: 'rgba(99, 102, 241, 0.05)',
                      borderRadius: '12px',
                      border: '1px dashed rgba(99, 102, 241, 0.2)'
                    }}>
                      <FaChartLine style={{ fontSize: '2.5rem', color: 'var(--primary-light)', opacity: 0.5, marginBottom: '1rem' }} />
                      <p style={{ color: 'rgba(226, 232, 240, 0.6)' }}>Graf aktivít bude tu</p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>

              <ScrollReveal delay={0.3}>
                <div className="glass-panel">
                  <div className="widget-header">
                    <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <FaCalendarCheck style={{ color: 'var(--secondary-color)' }} />
                      Najbližšie udalosti
                    </h3>
                  </div>
                  <div className="divider mt-3 mb-3"></div>
                  <div className="widget-content">
                    <div className="events-list">
                      {[
                        { time: '10:00', title: 'Stretnutie s tímom', location: 'Online meeting' },
                        { time: '14:30', title: 'Prezentácia projektu', location: 'Kancelária - miestnosť 201' }
                      ].map((event, idx) => (
                        <div key={idx} className="event-item">
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1rem',
                            padding: '1rem',
                            background: 'rgba(255, 255, 255, 0.02)',
                            borderRadius: '12px',
                            border: '1px solid rgba(255, 255, 255, 0.05)',
                            transition: 'all 0.3s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(99, 102, 241, 0.08)';
                            e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.2)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.02)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.05)';
                          }}
                          >
                            <div style={{
                              padding: '0.5rem 0.75rem',
                              background: 'var(--primary-color)',
                              borderRadius: '8px',
                              fontWeight: '600',
                              fontSize: '0.9rem',
                              color: 'white'
                            }}>
                              {event.time}
                            </div>
                            <div style={{ flex: 1 }}>
                              <h4 style={{ margin: 0, fontSize: '1rem', marginBottom: '0.25rem' }}>{event.title}</h4>
                              <p style={{ margin: 0, fontSize: '0.85rem', color: 'rgba(226, 232, 240, 0.6)' }}>{event.location}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
